import React, { useState, useEffect } from 'react';
import { Receipt, Search, CheckCircle } from 'lucide-react';
import api from '../../services/api';
import { DateFilter } from '../../components/dashboard/DateFilter';

const formatBRL = (value: number | string) =>
    `R$ ${(typeof value === 'string' ? parseFloat(value) : value).toFixed(2)}`;

const statusClass = (status: string) => {
    switch (String(status).toLowerCase()) {
        case 'paid':
        case 'completed':
            return 'bg-green-500/20 text-green-400';
        case 'pending':
            return 'bg-orange-500/20 text-orange-400';
        case 'failed':
        case 'refunded':
            return 'bg-red-500/20 text-red-400';
        default:
            return 'bg-gray-500/20 text-gray-400';
    }
};

const isPaidOrCompleted = (status: string) => {
    const s = String(status || '').toLowerCase();
    return s === 'paid' || s === 'completed';
};

export const TransactionsList: React.FC = () => {
    const [transactions, setTransactions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [syncingId, setSyncingId] = useState<number | string | null>(null);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);

    useEffect(() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        setStartDate(start.toISOString());
    }, []);

    const filtered = transactions.filter(tx => {
        if (!searchTerm.trim()) return true;
        const q = searchTerm.toLowerCase();
        return (tx.user_name || '').toLowerCase().includes(q) ||
            (tx.user_email || '').toLowerCase().includes(q) ||
            (tx.user_phone || '').toLowerCase().includes(q) ||
            (tx.bot_name || '').toLowerCase().includes(q) ||
            (tx.plan_name || '').toLowerCase().includes(q) ||
            (tx.external_id || '').toLowerCase().includes(q) ||
            (tx.status || '').toLowerCase().includes(q) ||
            (tx.payer_name || '').toLowerCase().includes(q) ||
            (tx.payer_email || '').toLowerCase().includes(q) ||
            (tx.payer_document || '').toLowerCase().includes(q);
    });

    useEffect(() => {
        if (startDate !== null || endDate !== null) {
            loadTransactions();
        }
    }, [startDate, endDate]);

    const loadTransactions = async () => {
        try {
            setLoading(true);
            const params: any = {};
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
            const res = await api.get('/banana/transactions', { params });
            setTransactions(res.data.transactions || []);
        } catch (error) {
            console.error('Erro ao carregar transações:', error);
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSyncLedger = async (tx: any) => {
        if (!tx?.id || syncingId !== null) return;
        setSyncingId(tx.id);
        setToast(null);
        try {
            const res = await api.post(`/banana/transactions/${tx.id}/sync-ledger`);
            setToast({ msg: res.data.message || 'Saldo creditado.', type: 'success' });
            await loadTransactions();
        } catch (err: any) {
            const msg = err?.response?.data?.error || 'Erro ao creditar saldo.';
            setToast({ msg, type: 'error' });
        } finally {
            setSyncingId(null);
        }
    };

    const handleApprove = async (tx: any) => {
        if (!tx?.id || syncingId !== null) return;
        setSyncingId(tx.id);
        setToast(null);
        try {
            const res = await api.post(`/banana/transactions/${tx.id}/approve`);
            setToast({ msg: res.data.message || 'Transação aprovada. Atualize "Saldo para saque" para ver o valor.', type: 'success' });
            await loadTransactions();
        } catch (err: any) {
            const msg = err?.response?.data?.error || 'Erro ao aprovar transação.';
            setToast({ msg, type: 'error' });
        } finally {
            setSyncingId(null);
        }
    };

    return (
        <div className="p-6">
            {toast && (
                <div className={`mb-4 px-4 py-2 rounded-xl text-sm ${toast.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {toast.msg}
                    <button type="button" onClick={() => setToast(null)} className="ml-2 underline">Fechar</button>
                </div>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                    <Receipt className="text-orange-500" /> Transações
                </h1>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar por usuário, ID, status ou pagador..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-xl py-2 pl-10 pr-4 text-white text-sm focus:border-orange-500/50 outline-none"
                        />
                    </div>
                    <button
                        onClick={loadTransactions}
                        className="bg-orange-600 hover:bg-orange-500 text-white rounded-xl px-4 py-2 text-xs font-bold transition-all"
                    >
                        Atualizar
                    </button>
                </div>
            </div>

            <DateFilter
                onFilterChange={(s, e) => {
                    setStartDate(s);
                    setEndDate(e);
                }}
            />

            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-x-auto mt-6">
                <table className="w-full text-left min-w-[900px]">
                    <thead className="text-xs text-gray-500 border-b border-white/10 bg-white/5">
                        <tr>
                            <th className="p-4 font-normal uppercase tracking-wider">Data</th>
                            <th className="p-4 font-normal uppercase tracking-wider">Bot</th>
                            <th className="p-4 font-normal uppercase tracking-wider">Plano</th>
                            <th className="p-4 font-normal uppercase tracking-wider">Usuário</th>
                            <th className="p-4 font-normal uppercase tracking-wider">Valor bruto</th>
                            <th className="p-4 font-normal uppercase tracking-wider">Taxa</th>
                            <th className="p-4 font-normal uppercase tracking-wider">Líquido</th>
                            <th className="p-4 font-normal uppercase tracking-wider">Pagador</th>
                            <th className="p-4 font-normal uppercase tracking-wider">Status</th>
                            <th className="p-4 font-normal uppercase tracking-wider">Método</th>
                            <th className="p-4 font-normal uppercase tracking-wider">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm">
                        {loading ? (
                            <tr>
                                <td colSpan={11} className="p-20 text-center text-gray-500">
                                    Carregando transações...
                                </td>
                            </tr>
                        ) : filtered.length === 0 ? (
                            <tr>
                                <td colSpan={11} className="p-20 text-center text-gray-500">
                                    {transactions.length === 0
                                        ? 'Nenhuma transação encontrada.'
                                        : `Nenhum resultado para "${searchTerm}".`}
                                </td>
                            </tr>
                        ) : (
                            filtered.map(tx => (
                                <tr
                                    key={tx.id}
                                    className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors"
                                >
                                    <td className="p-4 text-gray-400 whitespace-nowrap">
                                        {new Date(tx.created_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                    </td>
                                    <td className="p-4">
                                        <span className="text-gray-300 truncate block max-w-[120px]" title={tx.bot_name || ''}>
                                            {tx.bot_name || '—'}
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        <span className="text-gray-300 truncate block max-w-[120px]" title={tx.plan_name || ''}>
                                            {tx.plan_name || '—'}
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex flex-col">
                                            <span className="text-white font-medium">{tx.user_name || '—'}</span>
                                            <small className="text-gray-500 text-[10px] font-mono">{tx.user_email || tx.user_phone || '—'}</small>
                                        </div>
                                    </td>
                                    <td className="p-4 text-white font-medium">
                                        {formatBRL(tx.gross_amount ?? 0)}
                                    </td>
                                    <td className="p-4 text-orange-400">
                                        {formatBRL(tx.platform_fee ?? 0)}
                                    </td>
                                    <td className="p-4 text-green-400 font-medium">
                                        {formatBRL(tx.net_amount ?? 0)}
                                    </td>
                                    <td className="p-4">
                                        <div className="flex flex-col max-w-[160px]">
                                            <span className="text-gray-300 truncate">{tx.payer_name || '—'}</span>
                                            {(tx.payer_email || tx.payer_document) && (
                                                <small className="text-gray-500 text-[10px] font-mono truncate">
                                                    {tx.payer_email || tx.payer_document}
                                                </small>
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${statusClass(tx.status)}`}>
                                            {tx.status || '—'}
                                        </span>
                                    </td>
                                    <td className="p-4 text-gray-400 text-xs uppercase">
                                        {tx.payment_method || 'pix'}
                                    </td>
                                    <td className="p-4">
                                        <div className="flex flex-wrap items-center gap-2">
                                            {isPaidOrCompleted(tx.status) ? (
                                                <span className="inline-flex items-center gap-1.5 text-green-400 text-xs font-medium px-3 py-1.5 rounded-lg bg-green-500/10">
                                                    <CheckCircle size={14} />
                                                    Transação aprovada
                                                </span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => handleApprove(tx)}
                                                    disabled={syncingId === tx.id}
                                                    className="inline-flex items-center gap-1.5 bg-orange-600/20 hover:bg-orange-600/30 text-orange-400 rounded-lg px-3 py-1.5 text-xs font-medium transition-all disabled:opacity-50"
                                                    title="Marcar como paga e creditar valor líquido no saldo do usuário"
                                                >
                                                    <CheckCircle size={14} className={syncingId === tx.id ? 'animate-spin' : ''} />
                                                    {syncingId === tx.id ? '...' : 'Aprovar transação'}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
